import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../hooks/useAuth';
import logoImage from '../file/Drs.Farm.png';
import { normalizeInstagramUrl } from '../lib/social';
import { getResolvedHeaderTickerData } from '../lib/firestore';
import FullScreenMenu from './FullScreenMenu';

function RouteRobotsControl() {
  const location = useLocation();
  const shouldNoIndex = (
    location.pathname.startsWith('/admin')
    || location.pathname.startsWith('/me')
    || location.pathname.startsWith('/settings')
    || location.pathname.startsWith('/debug')
    || location.pathname === '/login'
  );

  if (!shouldNoIndex) return null;

  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
      <meta name="googlebot" content="noindex, nofollow" />
    </Helmet>
  );
}

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, isOperator, isFarmerRole } = useAuth();
  const instagramUrl = normalizeInstagramUrl(import.meta.env.VITE_INSTAGRAM_PROFILE_URL);
  const [tickerItems, setTickerItems] = useState([]);
  const [isMobileHeader, setIsMobileHeader] = useState(() => typeof window !== 'undefined' ? window.innerWidth <= 900 : false);

  useEffect(() => {
    let active = true;

    async function loadHeaderTicker() {
      try {
        const { items } = await getResolvedHeaderTickerData();
        if (!active) return;
        setTickerItems(items);
      } catch {
        if (active) setTickerItems([]);
      }
    }

    loadHeaderTicker();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobileHeader(window.innerWidth <= 900);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const tickerText = tickerItems.map((item) => item.text).join(' / ');
  const tickerTrackItems = tickerItems.length ? [...tickerItems, ...tickerItems] : [];
  const publicLinks = useMemo(() => ([
    { type: 'nav', to: '/', label: '理念' },
    { type: 'nav', to: '/farmers', label: '農家' },
    { type: 'nav', to: '/visibility', label: '生産の可視化' },
    { type: 'nav', to: '/doctors', label: 'ドクター' },
    { type: 'nav', to: '/posts', label: '記事' },
    { type: 'external', href: instagramUrl, label: 'Instagram' },
  ]), [instagramUrl]);
  const activeLinks = publicLinks;
  const publicDestination = location.pathname.startsWith('/farmers') ? '/doctors' : '/farmers';
  const publicLabel = location.pathname.startsWith('/farmers') ? 'ドクターを見る' : '農家を見る';
  const isFarmerUser = Boolean(user) && Boolean(isFarmerRole) && !isOperator;

  function handlePrimaryAction() {
    if (isFarmerUser) {
      navigate('/me');
      return;
    }

    if (!isOperator) {
      navigate(publicDestination);
      return;
    }

    if (location.pathname.startsWith('/admin')) {
      navigate(publicDestination);
      return;
    }

    navigate('/admin');
  }

  const primaryActionLabel = isFarmerUser
    ? 'メニュー'
    : isOperator
      ? (location.pathname.startsWith('/admin') ? publicLabel : '管理画面')
      : publicLabel;

  return (
    <>
      <header className="site-header">
        <div className="container nav-wrap">
          <NavLink to="/" className="brand" aria-label="Drs.Farm home">
            <img src={logoImage} alt="Drs.Farm" className="brand-logo" />
          </NavLink>
          {isMobileHeader ? (
            tickerItems.length ? (
              <div className="mobile-header-ticker" aria-label={tickerText}>
                <span className="mobile-header-ticker-track">
                  {tickerTrackItems.map((item, index) => (
                    <Link key={`${item.id}-${index}`} to={item.to} className="ticker-item-link">
                      {item.text}
                    </Link>
                  ))}
                </span>
              </div>
            ) : null
          ) : null}
          <div className="header-center">
            <nav aria-label="Main navigation">
              <ul className="nav-links">
                {activeLinks.map((item) => (
                  <li key={`${item.label}-${item.to || item.href}`}>
                    {item.type === 'nav' ? (
                      <NavLink to={item.to}>{item.label}</NavLink>
                    ) : (
                      <a href={item.href} target="_blank" rel="noreferrer">{item.label}</a>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
            {tickerItems.length ? (
              <div className="header-ticker-link" aria-label={tickerText}>
                <span className="header-ticker-track">
                  {tickerTrackItems.map((item, index) => (
                    <Link key={`${item.id}-${index}`} to={item.to} className="ticker-item-link">
                      {item.text}
                    </Link>
                  ))}
                </span>
              </div>
            ) : null}
          </div>
          <div className="auth-box">
            <button type="button" className="btn btn-primary" onClick={handlePrimaryAction}>{primaryActionLabel}</button>
            {user ? (
              <button type="button" className="btn btn-ghost" onClick={signOut}>ログアウト</button>
            ) : (
              <NavLink to="/login" className="btn btn-ghost">ログイン</NavLink>
            )}
          </div>
        </div>
      </header>

      <FullScreenMenu
        homeTo="/"
        logoSrc={logoImage}
        logoAlt="Drs.Farm"
        brandKicker="Drs.Farm"
        brandText="医師の視点と生産現場の知恵をつなぎ、食の信頼を積み上げるプロジェクトです。"
        primaryItems={activeLinks}
        secondaryItems={isOperator
          ? [{ type: 'action', label: primaryActionLabel, onClick: handlePrimaryAction }, { type: 'action', label: 'ログアウト', onClick: signOut }]
          : isFarmerUser
            ? [{ type: 'action', label: 'メニュー', onClick: handlePrimaryAction }, { type: 'action', label: 'ログアウト', onClick: signOut }]
            : user
              ? [{ type: 'action', label: 'ログアウト', onClick: signOut }]
              : [{ type: 'nav', to: '/login', label: 'ログイン' }]}
      />
    </>
  );
}

function Footer() {
  const instagramUrl = normalizeInstagramUrl(import.meta.env.VITE_INSTAGRAM_PROFILE_URL);
  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div className="footer-col">
          <img src={logoImage} alt="Drs.Farm" className="footer-logo" />
          <p>医師の視点と生産現場の知恵をつなぎ、食の信頼を積み上げるプロジェクトです。</p>
        </div>
        <div className="footer-col">
          <p><strong>問い合わせ:</strong> <a href="mailto:drs.farm.info@gmail.com">drs.farm.info@gmail.com</a></p>
          <p><strong>SNS:</strong> <a href={instagramUrl} target="_blank" rel="noreferrer">Instagram</a></p>
          <p><strong>TimeTree:</strong> <Link to="/timetree">リンク集を見る</Link></p>
          <p><strong>運営情報:</strong> Drs.Farm 事務局</p>
        </div>
      </div>
    </footer>
  );
}

function AppShell() {
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <>
      <a href="#main-content" className="skip-link">本文へスキップ</a>
      <RouteRobotsControl />
      <Navigation />
      <main id="main-content" className={`container page-main${isHome ? ' page-main-home' : ''}`}>
        <Outlet />
      </main>
      <Footer />
    </>
  );
}

export default function Layout() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
